// PET-672 — shared styling for the suggested-question colour palette. The
// backend stores stable colour tokens (see SUGGESTED_QUESTION_COLORS); the
// client owns how each token renders so the palette can be restyled without
// a data migration. Used by ManageQuestionsView (swatches), and SearchView /
// RoundtableView (grouped bubble clusters).
//
// NOTE: this is the one file allowed to carry literal Tailwind colour utility
// classes for this feature. It is a deliberate, documented exception to the
// "no hardcoded colours / DESIGN.md tokens" rule, scoped strictly to the
// suggested-question categorical tag palette — see ADR 0029.
export type QuestionColor =
  | "red"
  | "orange"
  | "amber"
  | "green"
  | "teal"
  | "blue"
  | "purple"
  | "pink";

type ColorStyle = {
  label: string;
  // Solid swatch (the picker dots).
  swatch: string;
  // Bubble appearance for the grouped display surfaces.
  bubble: string;
  // Small dot shown next to a group heading.
  dot: string;
};

const COLOR_STYLES: Record<QuestionColor, ColorStyle> = {
  red: {
    label: "Red",
    swatch: "bg-red-500",
    bubble:
      "border-red-300 bg-red-50 text-red-900 hover:bg-red-100 dark:border-red-900/50 dark:bg-red-950/40 dark:text-red-200 dark:hover:bg-red-950/70",
    dot: "bg-red-500",
  },
  orange: {
    label: "Orange",
    swatch: "bg-orange-500",
    bubble:
      "border-orange-300 bg-orange-50 text-orange-900 hover:bg-orange-100 dark:border-orange-900/50 dark:bg-orange-950/40 dark:text-orange-200 dark:hover:bg-orange-950/70",
    dot: "bg-orange-500",
  },
  amber: {
    label: "Amber",
    swatch: "bg-amber-500",
    bubble:
      "border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100 dark:border-amber-900/50 dark:bg-amber-950/40 dark:text-amber-200 dark:hover:bg-amber-950/70",
    dot: "bg-amber-500",
  },
  green: {
    label: "Green",
    swatch: "bg-green-500",
    bubble:
      "border-green-300 bg-green-50 text-green-900 hover:bg-green-100 dark:border-green-900/50 dark:bg-green-950/40 dark:text-green-200 dark:hover:bg-green-950/70",
    dot: "bg-green-500",
  },
  teal: {
    label: "Teal",
    swatch: "bg-teal-500",
    bubble:
      "border-teal-300 bg-teal-50 text-teal-900 hover:bg-teal-100 dark:border-teal-900/50 dark:bg-teal-950/40 dark:text-teal-200 dark:hover:bg-teal-950/70",
    dot: "bg-teal-500",
  },
  blue: {
    label: "Blue",
    swatch: "bg-blue-500",
    bubble:
      "border-blue-300 bg-blue-50 text-blue-900 hover:bg-blue-100 dark:border-blue-900/50 dark:bg-blue-950/40 dark:text-blue-200 dark:hover:bg-blue-950/70",
    dot: "bg-blue-500",
  },
  purple: {
    label: "Purple",
    swatch: "bg-purple-500",
    bubble:
      "border-purple-300 bg-purple-50 text-purple-900 hover:bg-purple-100 dark:border-purple-900/50 dark:bg-purple-950/40 dark:text-purple-200 dark:hover:bg-purple-950/70",
    dot: "bg-purple-500",
  },
  pink: {
    label: "Pink",
    swatch: "bg-pink-500",
    bubble:
      "border-pink-300 bg-pink-50 text-pink-900 hover:bg-pink-100 dark:border-pink-900/50 dark:bg-pink-950/40 dark:text-pink-200 dark:hover:bg-pink-950/70",
    dot: "bg-pink-500",
  },
};

// Decorative multi-colour swatch for the "All colours" filter affordance —
// a rainbow dot that signals "every colour" without singling one out. Kept
// here (not inline in a view) so ADR 0029 holds: this is the ONLY file
// allowed to carry literal palette colour utility classes for this feature.
export const MULTI_COLOR_SWATCH =
  "bg-gradient-to-br from-red-500 via-green-500 to-blue-500";

// Fallback styling for the "no colour" group / entries.
const UNCOLORED: ColorStyle = {
  label: "No colour",
  swatch: "bg-muted",
  bubble: "border-border bg-muted text-foreground hover:bg-muted/70",
  dot: "bg-muted-foreground/40",
};

export function isQuestionColor(value: unknown): value is QuestionColor {
  return typeof value === "string" && value in COLOR_STYLES;
}

export function colorStyle(color: string | null): ColorStyle {
  return isQuestionColor(color) ? COLOR_STYLES[color] : UNCOLORED;
}

export function colorLabel(color: string | null): string {
  return colorStyle(color).label;
}

// Palette order used to render colour groups consistently across surfaces.
export const PALETTE_ORDER: QuestionColor[] = [
  "red",
  "orange",
  "amber",
  "green",
  "teal",
  "blue",
  "purple",
  "pink",
];

// Group items by their colour token, preserving each item's original order
// within a group. Coloured groups come first in palette order; uncoloured
// (null) items land in a final group. Empty groups are omitted.
export function groupByColor<T>(
  items: readonly T[],
  getColor: (item: T) => string | null,
): { color: string | null; items: T[] }[] {
  const byColor = new Map<string | null, T[]>();
  for (const item of items) {
    const raw = getColor(item);
    const key = isQuestionColor(raw) ? raw : null;
    const bucket = byColor.get(key);
    if (bucket) bucket.push(item);
    else byColor.set(key, [item]);
  }
  const groups: { color: string | null; items: T[] }[] = [];
  for (const color of PALETTE_ORDER) {
    const bucket = byColor.get(color);
    if (bucket && bucket.length > 0) groups.push({ color, items: bucket });
  }
  const uncolored = byColor.get(null);
  if (uncolored && uncolored.length > 0)
    groups.push({ color: null, items: uncolored });
  return groups;
}
